import { put, takeLatest, select } from "typed-redux-saga";
import {
  ADD_TO_BASKET,
  DELETE_BASKET,
  DELETE_INDIVIDUAL_OPTIONS,
  EDIT_BASKET,
  EDIT_INDIVIDUAL_OPTIONS,
} from "../constants/actionTypes";
import {
  ADD_TO_BASKET_ASYNC,
  CLEANUP_ASYNC,
  CLEANUP_BY_LINE,
  COPY_LINE_OPTIONS,
  COPY_OPTIONS,
  DELETE_BASKET_ASYNC,
  DELETE_LINE_OPTIONS_ASYNC,
  DELETE_OPTION_LINE,
  DELETE_OPTION_LINE_ASYNC,
  DELETE_SAVED_LINE_OPTIONS,
  MODIFY_OPTIONS,
  MODIFY_OPTIONS_ASYNC,
  REVERT_CHILD_LINES_ASYNC,
  UPDATE_ALL_OPTIONS_ASYNC,
  UPDATE_INDIVIDUAL_OPTIONS_ASYNC,
  UPDATE_OPTION_FOR_LINE,
} from "../constants/actionTypesTpos";
import {
  AddToBasketAsyncAction,
  DeleteBasketAsyncAction,
  DeleteLineOptionsAsyncAction,
  DeleteOptionLineAsyncAction,
  ModifyOptionsAsyncAction,
  UpdateAllOptionsAsyncAction,
} from "../types/option";
import {
  getDanglingLineItemByParent,
  getIndividualItems,
} from "../selectors/lineItem";
import { all } from "redux-saga/effects";
import {
  getAllCurrentLineOptions,
  getAllLineItems,
  getAllOptions,
  getOptionsByLine,
} from "../selectors/options";
import { deleteOptionLineAsync } from "../actions/options";

export default function* optionsSaga(): Generator {
  yield* takeLatest(
    UPDATE_INDIVIDUAL_OPTIONS_ASYNC,
    handleUpdateIndividualOptions,
  );
  yield* takeLatest(CLEANUP_BY_LINE, handleCleanupByLine);
  yield* takeLatest(UPDATE_ALL_OPTIONS_ASYNC, handleUpdateAllOptionsAsync);
  yield* takeLatest(CLEANUP_ASYNC, handleCleanupAsync);
  yield* takeLatest(DELETE_LINE_OPTIONS_ASYNC, handleDeleteOptionsForLine);
  yield* takeLatest(REVERT_CHILD_LINES_ASYNC, handleRevertChildLinesAsync);
  yield* takeLatest(DELETE_OPTION_LINE_ASYNC, handleDeleteOptionLineAsync);
  yield* takeLatest(DELETE_BASKET_ASYNC, handleDeleteBasketAsync);
  yield* takeLatest(ADD_TO_BASKET_ASYNC, handleAddToBasketAsync);
  yield* takeLatest(MODIFY_OPTIONS_ASYNC, handleModifyOptionsAsync);
}

function* handleUpdateIndividualOptions({ lineItemId }: any): Generator {
  const lines = yield* select(getIndividualItems, {
    lineItemId,
  });

  yield all(
    lines.map((lineItem) => {
      return put({
        type: UPDATE_OPTION_FOR_LINE,
        payload: { lineId: lineItem.id },
      });
    }),
  );
  yield put({
    type: CLEANUP_BY_LINE,
    lineItemId,
  });
}

function* handleCleanupByLine({ lineItemId }: any): Generator {
  const lines = yield* select(getIndividualItems, {
    lineItemId,
  });
  yield all(
    lines.map((lineItem) => {
      return put({
        type: COPY_OPTIONS,
        payload: { lineId: lineItem.id, options: undefined },
      });
    }),
  );
}

function* handleCleanupAsync({ lineItemId }: any): Generator {
  yield put({
    type: CLEANUP_BY_LINE,
    lineItemId,
  });
  yield put({
    type: COPY_OPTIONS,
    payload: {
      lineId: lineItemId,
      options: undefined,
    },
  });
}

function* handleDeleteOptionsForLine({
  lineItemId,
}: DeleteLineOptionsAsyncAction): Generator {
  const lines = yield* select(getIndividualItems, {
    lineItemId: `${lineItemId}`,
  });
  yield all(
    lines.map((lineItem) => {
      return put({
        type: DELETE_SAVED_LINE_OPTIONS,
        payload: { lineId: lineItem.id },
      });
    }),
  );
}

function* handleUpdateAllOptionsAsync({
  payload: { lineId, item, quantity },
}: UpdateAllOptionsAsyncAction): Generator {
  yield put({
    type: UPDATE_OPTION_FOR_LINE,
    payload: { lineId },
  });
  yield put({
    type: EDIT_BASKET,
    payload: {
      lineId,
      item,
      quantity,
    },
  });
  yield put({
    type: DELETE_LINE_OPTIONS_ASYNC,
    lineItemId: lineId,
  });
  yield put({
    type: DELETE_INDIVIDUAL_OPTIONS,
    payload: lineId,
  });
}

function* handleRevertChildLinesAsync({ lineItemId }: any): Generator {
  const lines = yield* select(getIndividualItems, {
    lineItemId,
  });
  const lineOptions = yield* select(getAllOptions);
  const doesOptionExist = lines.filter((lineItem) => lineOptions[lineItem.id]);
  if (!doesOptionExist || doesOptionExist.length === 0) {
    yield put({
      type: DELETE_INDIVIDUAL_OPTIONS,
      payload: lineItemId,
    });
  }
}

function* handleDeleteOptionLineAsync({
  lineId,
}: DeleteOptionLineAsyncAction): Generator {
  const siblingLine = yield* select(getDanglingLineItemByParent, {
    lineItemId: lineId,
  });
  if (siblingLine) {
    const currentLineOptions = yield* select(getAllCurrentLineOptions);
    if (currentLineOptions) {
      yield put({
        type: COPY_LINE_OPTIONS,
        payload: {
          lineId: siblingLine.parentId,
          options: currentLineOptions[siblingLine.id],
        },
      });
    }
  }
  yield put({
    type: DELETE_OPTION_LINE,
    payload: Number(lineId),
  });
  yield put({
    type: DELETE_SAVED_LINE_OPTIONS,
    payload: { lineId },
  });
  if (siblingLine) {
    yield put(deleteOptionLineAsync(`${siblingLine.id}`));
  }
}

function* handleDeleteBasketAsync({
  payload: { lineId },
}: DeleteBasketAsyncAction): Generator {
  yield put({
    type: DELETE_LINE_OPTIONS_ASYNC,
    lineItemId: lineId,
  });
  yield put({
    type: DELETE_INDIVIDUAL_OPTIONS,
    payload: lineId,
  });
  yield put({ type: DELETE_OPTION_LINE_ASYNC, lineId });
  yield put({
    type: DELETE_BASKET,
    payload: { lineId },
  });
}

export function* handleAddToBasketAsync({
  payload: { item, variationId, quantity, comboSelections },
}: AddToBasketAsyncAction): Generator {
  if (comboSelections) {
    yield put({
      type: ADD_TO_BASKET,
      payload: { item, variationId, quantity, comboSelections },
    });
    return;
  }

  let allLineItems = yield* select(getAllLineItems);
  let basketItem = allLineItems.find(
    (lineItem) =>
      lineItem.productId === item.id && lineItem.variationId === variationId,
  );

  if (basketItem?.id && !basketItem.children) {
    const lineOptions = yield* select(getOptionsByLine, {
      lineItemId: `${basketItem?.id}`,
    });
    if (lineOptions.length > 0) {
      yield put({
        type: COPY_OPTIONS,
        payload: {
          lineId: basketItem.id,
          options: lineOptions,
        },
      });
      yield put({
        type: EDIT_INDIVIDUAL_OPTIONS,
        payload: basketItem.id,
      });
      allLineItems = yield* select(getAllLineItems);
      basketItem = allLineItems.find(
        (lineItem) =>
          lineItem.productId === item.id &&
          lineItem.variationId === variationId,
      );

      if (basketItem?.children) {
        yield all(
          basketItem?.children?.map((childLine) => {
            return put({
              type: COPY_OPTIONS,
              payload: {
                lineId: childLine,
                options: lineOptions,
              },
            });
          }),
        );
        yield all(
          basketItem?.children?.map((childLine) => {
            return put({
              type: UPDATE_OPTION_FOR_LINE,
              payload: {
                lineId: childLine,
              },
            });
          }),
        );
      }

      yield put({
        type: DELETE_SAVED_LINE_OPTIONS,
        payload: { lineId: basketItem?.id },
      });
    }
  } else if (basketItem?.id && basketItem.children) {
    yield put({
      type: DELETE_SAVED_LINE_OPTIONS,
      payload: { lineId: basketItem?.id },
    });
  }

  yield put({
    type: ADD_TO_BASKET,
    payload: { item, variationId, quantity },
  });
}

function* handleModifyOptionsAsync({
  payload,
}: ModifyOptionsAsyncAction): Generator {
  const { lineId } = payload;
  yield put({
    type: MODIFY_OPTIONS,
    payload,
  });
  yield put({
    type: UPDATE_OPTION_FOR_LINE,
    payload: { lineId },
  });
}
